import { IonApp, IonRouterOutlet } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import type { ReactNode } from 'react';
import { Redirect, Route } from 'react-router-dom';

import { CompanyFromUrl } from '~/components/CompanyFromUrl';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { NetworkBanner } from '~/components/NetworkBanner';
import { RequireAuth } from '~/components/RequireAuth';
import { RequireModule } from '~/components/RequireModule';
import { TenantGate } from '~/components/TenantGate';
import { Convite } from '~/pages/convite/Convite';
import { Dashboard } from '~/pages/dashboard/Dashboard';
import { Feed } from '~/pages/documentos/Feed';
import { Pastas } from '~/pages/documentos/Pastas';
import { Emitir } from '~/pages/emitir/Emitir';
import { ConvidarUsuario } from '~/pages/empresa/ConvidarUsuario';
import { EditarUsuario } from '~/pages/empresa/EditarUsuario';
import { Empresa } from '~/pages/empresa/Empresa';
import { EmpresaUsuarios } from '~/pages/empresa/EmpresaUsuarios';
import { Impostos } from '~/pages/impostos/Impostos';
import { Login } from '~/pages/login/Login';
import { NotaDetalhe } from '~/pages/notas/NotaDetalhe';
import { Notas } from '~/pages/notas/Notas';
import { Perfil } from '~/pages/perfil/Perfil';
import { ROUTES } from '~/routes';
import { ACCESS_LEVEL } from '~/session/access';

function Protected({ children }: { children: ReactNode }): JSX.Element {
  return (
    <RequireAuth>
      <ErrorBoundary>{children}</ErrorBoundary>
    </RequireAuth>
  );
}

export function App(): JSX.Element {
  return (
    <IonApp>
      {/* Overlay global de status — fica fixo no topo sobre o router.
          NetworkBanner já é silencioso quando não tem nada a mostrar. */}
      <div
        style={{
          position: 'fixed',
          top: 'env(safe-area-inset-top, 0)',
          left: 0,
          right: 0,
          zIndex: 1000,
          pointerEvents: 'none',
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <NetworkBanner />
        </div>
      </div>

      {/* Nenhuma tela antes do escritório do endereço: login, marca e sessão dependem dele. */}
      <TenantGate>
        <IonReactRouter>
          <IonRouterOutlet>
            <Route exact path={ROUTES.login} component={Login} />
            {/* Pública: o convidado ainda não tem conta, e o token do e-mail é a credencial. */}
            <Route exact path={ROUTES.convite} component={Convite} />
            <Route exact path={ROUTES.dashboard}>
              <Protected>
                <Dashboard />
              </Protected>
            </Route>
            <Route exact path={ROUTES.emitir}>
              <Protected>
                <RequireModule module="nfse" level={ACCESS_LEVEL.full}>
                  <Emitir />
                </RequireModule>
              </Protected>
            </Route>
            <Route exact path={ROUTES.notas}>
              <Protected>
                <RequireModule module="nfse">
                  <Notas />
                </RequireModule>
              </Protected>
            </Route>
            <Route exact path={ROUTES.notaDetalhe}>
              <Protected>
                <RequireModule module="nfse">
                  <NotaDetalhe />
                </RequireModule>
              </Protected>
            </Route>
            <Route exact path={ROUTES.empresa}>
              <Protected>
                <Empresa />
              </Protected>
            </Route>
            <Route exact path={ROUTES.empresaUsuarios}>
              <Protected>
                <RequireModule module="company_users" level={ACCESS_LEVEL.full}>
                  <EmpresaUsuarios />
                </RequireModule>
              </Protected>
            </Route>
            <Route exact path={ROUTES.empresaUsuario}>
              <Protected>
                <RequireModule module="company_users" level={ACCESS_LEVEL.full}>
                  <EditarUsuario />
                </RequireModule>
              </Protected>
            </Route>
            {/* Depois de ":id": o outlet fica com a última rota que casa, e o caminho
                literal tem de vencer se um dia o `:id` deixar de exigir dígitos. */}
            <Route exact path={ROUTES.empresaUsuariosConvidar}>
              <Protected>
                <RequireModule module="company_users" level={ACCESS_LEVEL.full}>
                  <ConvidarUsuario />
                </RequireModule>
              </Protected>
            </Route>
            {/* O push de um post e o de um lembrete abrem estas duas com `?empresa=<id>`: a
                empresa troca antes do guarda, que já lê o nível na empresa nova. */}
            <Route exact path={ROUTES.documentos}>
              <Protected>
                <CompanyFromUrl>
                  <RequireModule module="documents">
                    <Feed />
                  </RequireModule>
                </CompanyFromUrl>
              </Protected>
            </Route>
            <Route exact path={ROUTES.documentosPastas}>
              <Protected>
                <RequireModule module="documents">
                  <Pastas />
                </RequireModule>
              </Protected>
            </Route>
            <Route exact path={ROUTES.impostos}>
              <Protected>
                <CompanyFromUrl>
                  <RequireModule module="documents">
                    <Impostos />
                  </RequireModule>
                </CompanyFromUrl>
              </Protected>
            </Route>
            <Route exact path={ROUTES.perfil}>
              <Protected>
                <Perfil />
              </Protected>
            </Route>
            <Route>
              <Redirect to={ROUTES.dashboard} />
            </Route>
          </IonRouterOutlet>
        </IonReactRouter>
      </TenantGate>
    </IonApp>
  );
}
